import { Inject, Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Actions, createEffect, ofType, ROOT_EFFECTS_INIT } from '@ngrx/effects';
import { StorageMap } from '@ngx-pwa/local-storage';
import { EMPTY, of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { WINDOW, WindowWrapper } from './../../common/services/window.service';
import { UserSession } from './../models/SessionUser';
import { GaSessionConfigService } from './../services/session.config.service';
import { GaSessionDataService } from './../services/session.data.service';
import * as sessionStore from './session.store';

@Injectable()
export class SessionEffects {
    constructor(
        private actions$: Actions,
        private route: ActivatedRoute,
        private router: Router,
        private sessionConfig: GaSessionConfigService,
        private sessionsDataService: GaSessionDataService,
        private storage: StorageMap,
        @Inject(WINDOW) private window: WindowWrapper
    ) { }

    restore$ = createEffect(() => this.actions$.pipe(
        ofType(sessionStore.restoreAction, ROOT_EFFECTS_INIT),
        switchMap(action => this.storage.get<UserSession>(this.sessionConfig.lsKeys.session).pipe(
            map((session: UserSession) => {
                if (session) {
                    return sessionStore.restoreSuccessAction({ sessionUser: session });
                } else if (this.sessionConfig.enabled) {
                    return sessionStore.restoreFailureAction({ error: 'Session missed' });
                } else {
                    return sessionStore.restoreSuccessAction({ sessionUser: null });
                }
            })
        ))
    ));

    signin$ = createEffect(() => this.actions$.pipe(
        ofType(sessionStore.signinAction, sessionStore.signinAbortAction),
        switchMap(action => {
            if (action.type === sessionStore.SessionActionTypes.Signin) {
                return this.sessionsDataService.signin(action.login, action.password).pipe(
                    map(sessionUser => sessionStore.signinSuccessAction({ sessionUser })),
                    catchError(error => of(sessionStore.signinFailureAction({ error })))
                );
            } else {
                return EMPTY;
            }
        })
    ));

    signout$ = createEffect(() => this.actions$.pipe(
        ofType(sessionStore.signoutAction),
        switchMap(action => this.sessionsDataService.signout().pipe(
            map(() => sessionStore.signoutSuccessAction()),
            catchError(error => of(sessionStore.signoutFailureAction({ error })))
        ))
    ));

    authorizedRedirect$ = createEffect(() => this.actions$.pipe(
        ofType(sessionStore.signinSuccessAction, sessionStore.signupSuccessAction),
        switchMap(action => this.storage.set('session', action.sessionUser)),
        tap(() => {
            const url = this.route.snapshot.queryParamMap.get('from');
            this.window.location.href = url ? decodeURIComponent(url) : this.window.location.origin + this.sessionConfig.autorizedUrl;
        })
    ), { dispatch: false });

    unauthorizedRedirect$ = createEffect(() => this.actions$.pipe(
        ofType(sessionStore.signoutSuccessAction, sessionStore.restoreFailureAction),
        switchMap(() => this.storage.delete('session')),
        tap(() => {
            this.window.location.href = this.window.location.origin +
                this.sessionConfig.unautorizedUrl +
                '?from=' +
                encodeURIComponent(this.router.url);
        })
    ), { dispatch: false });

}
